/**
 * Entry Transition - The "Arrive..." Gate
 * 
 * CRITICAL COMPONENT for Reclaim Our Presence
 * This is the 1-2 second soft fade that separates the user from normal app usage
 * before entering any immersive presence experience.
 * 
 * Features:
 * - Soft fade to dark
 * - Breath animation (expanding circle)
 * - "Arrive..." text that fades in
 * - Slow, deliberate pacing
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const EntryTransition = ({ isActive, onComplete, duration = 2500 }) => {
  const [phase, setPhase] = useState('fade'); // 'fade' | 'breath' | 'text' | 'complete'

  useEffect(() => {
    if (!isActive) {
      setPhase('fade');
      return;
    }

    // Phase 1: Fade in (0-500ms)
    const fadeTimer = setTimeout(() => setPhase('breath'), 500);
    
    // Phase 2: Breath animation starts (500-1500ms)
    const breathTimer = setTimeout(() => setPhase('text'), 1200);
    
    // Phase 3: "Arrive..." appears (1200-2000ms)
    const textTimer = setTimeout(() => setPhase('complete'), 2000);
    
    // Phase 4: Complete and call onComplete (2000-2500ms)
    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, duration);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(breathTimer);
      clearTimeout(textTimer);
      clearTimeout(completeTimer);
    };
  }, [isActive, onComplete, duration]);

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A0A0A]"
          data-testid="entry-transition"
        >
          {/* Subtle radial gradient background */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(74,103,65,0.08) 0%, transparent 60%)'
            }}
          />

          {/* Breath Circle Animation */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ 
              scale: phase === 'fade' ? 0.5 : [0.8, 1.1, 0.9, 1],
              opacity: phase === 'fade' ? 0 : [0.3, 0.5, 0.4, 0.3]
            }}
            transition={{ 
              duration: 2,
              ease: 'easeInOut',
              times: [0, 0.4, 0.7, 1]
            }}
            className="absolute w-32 h-32 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(74,103,65,0.15) 0%, transparent 70%)',
              boxShadow: '0 0 80px rgba(74,103,65,0.1)'
            }}
          />

          {/* "Arrive..." Text */}
          <AnimatePresence>
            {(phase === 'text' || phase === 'complete') && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="text-center z-10"
              >
                <p 
                  className="font-['Playfair_Display'] text-2xl text-white/80 italic tracking-wide"
                  style={{ textShadow: '0 0 40px rgba(74,103,65,0.3)' }}
                >
                  Arrive...
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Subtle noise texture overlay */}
          <div 
            className="absolute inset-0 pointer-events-none opacity-[0.03]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EntryTransition;
